/**
 * Product Card Variant Picker
 * Lightweight variant picker specifically for product cards in collection grids
 * Handles variant selection, disabled variants, and form submission
 */

if (!customElements.get("product-card-variant-picker")) {
  class ProductCardVariantPicker extends HTMLElement {
    constructor() {
      super();
      this.container = this.closest("[data-variant-picker]");
      this.productId = this.container?.dataset.productId;
      this.variants = this.getVariantData();
      this.currentVariant = null;
    }

    connectedCallback() {
      this.setupEventListeners();
      this.initializeVariant();
    }

    setupEventListeners() {
      this.addEventListener("change", this.onVariantChange.bind(this));
    }

    getVariantData() {
      const script = this.container?.querySelector(
        '#productVariants[type="application/json"]'
      );
      return script ? JSON.parse(script.textContent) : [];
    }

    initializeVariant() {
      // Don't pre-select any variants - keep all options visible but unselected
      // Just ensure the availability styling is applied based on template data
      this.updateOptionAvailability();
    }

    onVariantChange(event) {
      if (!event.target.matches('input[type="radio"]')) return;

      const selectedOptions = this.getSelectedOptions();
      this.currentVariant = this.getVariantFromOptions(selectedOptions);

      this.updateVariantInput();
      this.updateOptionAvailability();
      this.handleFormSubmission(event);
    }

    getSelectedOptions() {
      const pickerFields = this.querySelectorAll("[data-picker-field='radio']");
      return Array.from(pickerFields).map((field) => {
        const checkedInput = field.querySelector('input[type="radio"]:checked');
        return checkedInput ? checkedInput.value : null;
      });
    }

    getVariantFromOptions(options) {
      return this.variants.find((variant) => {
        return options.every((option, index) => {
          return !option || variant.options[index] === option;
        });
      });
    }

    updateVariantInput() {
      const form = this.closest("form");
      if (!form) return;

      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput && this.currentVariant) {
        variantInput.value = this.currentVariant.id;
        variantInput.disabled = false;
      }
    }

    updateOptionAvailability() {
      const pickerFields = this.querySelectorAll("[data-picker-field='radio']");

      pickerFields.forEach((field, optionIndex) => {
        const options = field.querySelectorAll(".m-product-option--node");

        options.forEach((optionNode) => {
          const input = optionNode.querySelector('input[type="radio"]');
          const label = optionNode.querySelector(
            ".m-product-option--node__label"
          );
          const value = input?.value;

          if (!value) return;

          // Use the data-available attribute set by the Liquid template
          // This maintains the original availability state without hiding options
          const isAvailable = optionNode.dataset.available === "true";

          // Update option styling and availability based on template data
          if (isAvailable) {
            optionNode.classList.remove("m-product-option--node--disabled");
            optionNode.classList.remove("m-product-option--node__soldout");
            input.disabled = false;
            if (label) {
              label.classList.remove("m-product-option--node__label--disabled");
              label.classList.remove("m-product-option--node__soldout");
            }
          } else {
            optionNode.classList.add("m-product-option--node--disabled");
            optionNode.classList.add("m-product-option--node__soldout");
            input.disabled = true;
            if (label) {
              label.classList.add("m-product-option--node__label--disabled");
              label.classList.add("m-product-option--node__soldout");
            }
          }
        });
      });
    }

    handleFormSubmission(event) {
      const form = this.closest("form");
      const mobileModal = form?.closest(".m-product-card__mobile-modal");

      if (mobileModal) {
        // Mobile modal: auto-submit like desktop hover for consistent behavior
        this.autoSubmitForm(form);
      } else {
        // Desktop hover: auto-submit after delay
        this.autoSubmitForm(form);
      }
    }

    autoSubmitForm(form) {
      if (!form || !this.currentVariant || !this.currentVariant.available)
        return;

      // Prevent rapid multiple submissions
      if (form.submitting) return;

      setTimeout(() => {
        const productForm = form.closest("product-form");
        const variantInput = form.querySelector('input[name="id"]');
        const mobileModal = form?.closest(".m-product-card__mobile-modal");

        if (variantInput && variantInput.value && !variantInput.disabled) {
          if (productForm) {
            // Set up mobile modal closing if this is in a mobile modal
            if (mobileModal) {
              this.setupMobileModalClosing(productForm);
            }

            // Use theme's native product-form submission
            this.triggerThemeFormSubmission(form);
          } else {
            // Fallback: trigger form submission event
            const submitEvent = new Event("submit", {
              bubbles: true,
              cancelable: true,
            });
            form.dispatchEvent(submitEvent);
          }
        }
      }, 100);
    }

    setupMobileModalClosing(productForm) {
      // Set up one-time event listeners for mobile modal closing
      const handleSuccess = () => {
        // Close mobile modal on successful submission
        if (window.MobileProductCardModal) {
          window.MobileProductCardModal.closeModal();
        }

        // Clean up listeners
        productForm.removeEventListener("submit", handleFormSubmit);
        document.removeEventListener("cart-update", handleSuccess);
        // Also try listening for MinimogTheme cart events
        if (window.MinimogEvents && window.MinimogTheme) {
          window.MinimogEvents.off(
            window.MinimogTheme.pubSubEvents.cartUpdate,
            handleSuccess
          );
        }
      };

      const handleFormSubmit = (e) => {
        // Listen for multiple cart update events to ensure we catch the success
        document.addEventListener("cart-update", handleSuccess, { once: true });

        // Also listen for theme's native cart events
        if (window.MinimogEvents && window.MinimogTheme) {
          window.MinimogEvents.subscribe(
            window.MinimogTheme.pubSubEvents.cartUpdate,
            handleSuccess
          );
        }

        // Fallback: close modal after a delay if other events don't fire
        setTimeout(() => {
          if (
            window.MobileProductCardModal &&
            window.MobileProductCardModal.activeModal
          ) {
            window.MobileProductCardModal.closeModal();
          }
        }, 2000);
      };

      // Listen for form submission
      productForm.addEventListener("submit", handleFormSubmit, { once: true });
    }

    triggerThemeFormSubmission(form) {
      const submitEvent = new Event("submit", {
        bubbles: true,
        cancelable: true,
      });

      form.dispatchEvent(submitEvent);

      // Reset variant selection after successful submission
      // Longer delay for mobile modal to ensure it closes first
      const mobileModal = form?.closest(".m-product-card__mobile-modal");
      const delay = mobileModal ? 2500 : 1000;

      setTimeout(() => {
        this.resetVariantSelection(form);
      }, delay);
    }

    resetVariantSelection(form) {
      // Reset all radio buttons to unchecked (but maintain disabled state)
      const radioInputs = form.querySelectorAll('input[type="radio"]');
      radioInputs.forEach((input) => {
        input.checked = false;
        // Maintain disabled state based on availability
        const optionNode = input.closest(".m-product-option--node");
        const isAvailable = optionNode?.dataset.available === "true";
        input.disabled = !isAvailable;
      });

      // Reset the label text back to dynamic default based on option name
      const optionLabels = form.querySelectorAll(".option-label--selected");
      optionLabels.forEach((label) => {
        const optionTitle = label.parentElement.querySelector(
          ".option-label--title"
        );
        if (optionTitle) {
          const optionName = optionTitle.textContent
            .replace(":", "")
            .toLowerCase();
          label.textContent = `Select ${optionName}`;
        } else {
          label.textContent = "Select option";
        }
      });

      // Clear and disable the hidden variant input
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = "";
        variantInput.disabled = true;
      }

      // Update picker field selected values
      const pickerFields = form.querySelectorAll("[data-picker-field]");
      pickerFields.forEach((field) => {
        field.setAttribute("data-selected-value", "");
      });

      // Clear current variant
      this.currentVariant = null;

      // Restore proper disabled styling for unavailable options
      const optionNodes = form.querySelectorAll(".m-product-option--node");
      optionNodes.forEach((node) => {
        const isAvailable = node.dataset.available === "true";
        const label = node.querySelector(".m-product-option--node__label");

        if (!isAvailable) {
          node.classList.add("m-product-option--node--disabled");
          if (label) {
            label.classList.add("m-product-option--node__label--disabled");
          }
        } else {
          node.classList.remove("m-product-option--node--disabled");
          if (label) {
            label.classList.remove("m-product-option--node__label--disabled");
          }
        }
      });
    }
  }

  customElements.define(
    "product-card-variant-picker",
    ProductCardVariantPicker
  );
}

/**
 * Product Card Variant Button
 * Lightweight variant button for product cards
 */
if (!customElements.get("product-card-variant-button")) {
  class ProductCardVariantButton extends HTMLElement {
    constructor() {
      super();
      this.selectedSpan = this.querySelector(".option-label--selected");
      this.addEventListener("change", this.updateSelectedValue.bind(this));
    }

    updateSelectedValue() {
      const checkedInput = this.querySelector('input[type="radio"]:checked');
      if (checkedInput) {
        this.value = checkedInput.value;
        this.setAttribute("data-selected-value", this.value);

        if (this.selectedSpan) {
          this.selectedSpan.textContent = this.value;
          this.selectedSpan.classList.remove("m:text-color-error");
        }
      }
    }
  }

  customElements.define(
    "product-card-variant-button",
    ProductCardVariantButton
  );
}

// Initialize on DOM ready
document.addEventListener("DOMContentLoaded", () => {
  console.log("Product Card Variant Picker initialized");
});
